import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { HelpOutline } from '@mui/icons-material'
import { Box, Button, Card, CardContent, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { useGetGenreTaxonomyQuery } from '../../../../api/core'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { borderRadiusDefault } from '../../../../themes/theme'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { ConventionalCategory, ConventionalGenre, ConventionalSubgenre } from '../../../genre-taxonomy/types/GenreTaxonomy'
import { SegmentFilterRow } from '../SegmentFilterRow/SegmentFilterRow'
import { TaxonomyCounts } from '../helpers/catalogueUtils'
import { FILTER_CHIP_HEIGHT, FILTER_ROW_LABEL_WIDTH, filterBarSurfaceSx } from '../helpers/filterChipStyles'
import {
  SegmentOption,
  categoryOptionGroups,
  genreOptionGroups,
  inheritedGenreScopes,
  inheritedSubgenreScopes,
  pruneAndOrderGroups,
  subgenreOptionGroups,
} from '../helpers/segmentFilterOptions'

export interface CategoryGenreSelection {
  conventionalCategoryIds: string[]
  conventionalGenreIds: string[]
  conventionalSubgenreIds: string[]
}

export const emptyCategoryGenreSelection: CategoryGenreSelection = {
  conventionalCategoryIds: [],
  conventionalGenreIds: [],
  conventionalSubgenreIds: [],
}

interface MarketAnalyticsFilterBarProps {
  selectedCategoryIds: string[]
  selectedGenreIds: string[]
  selectedSubgenreIds: string[]
  // Each row applies its whole level at once: the "+ Add" picker stages its
  // ticks and commits them on close, so nothing shifts mid-selection.
  onSetCategories: (ids: string[]) => void
  onSetGenres: (ids: string[]) => void
  onSetSubgenres: (ids: string[]) => void
  onClearCategories: () => void
  onClearGenres: () => void
  onClearSubgenres: () => void
  onClearTaxonomy: () => void
  taxonomyCounts?: TaxonomyCounts
  // The cohort behind every adoption figure in the tool (`cohortGamesCount`):
  // games in the resolved scope tracked on or before the selected period's first
  // day. It therefore moves with the window chip, not only with the taxonomy —
  // hence the tooltip. `null`/`undefined` while it is unknown.
  cohortGamesCount?: number | null
  // Whether an unknown cohort is still being fetched. When false, an unknown
  // cohort (a finished rollup with no rows, or a failed one) shows an em dash
  // rather than a skeleton that never resolves. Omitted: an unknown cohort is
  // treated as loading.
  isCohortLoading?: boolean
}

const CATEGORY_ROW_LABEL_ID = 'mac-fb-category-label'
const GENRE_ROW_LABEL_ID = 'mac-fb-genre-label'
const SUBGENRE_ROW_LABEL_ID = 'mac-fb-subgenre-label'

// Outer row: fixed label column + a chips column that wraps on its own + the
// count on the right. Aligned to the top so the label and count sit on the
// first chip line, and set to nowrap so the chip column never collapses under
// the label.
const rowSx = {
  display: 'flex',
  flexWrap: 'nowrap' as const,
  alignItems: 'flex-start',
  columnGap: 1.5,
}

const stackedRowSx = {
  ...rowSx,
  mt: 1.5,
  pt: 1.5,
  borderTop: '1px dashed',
  borderColor: 'divider',
}

const footerSx: SxProps<Theme> = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: 1,
  px: 2,
  py: 1,
  borderTop: `1px solid ${theme.palette.divider}`,
  // Faint wash of the page background, so the footer reads as chrome below the
  // rows without becoming a second card.
  bgcolor: alpha(theme.palette.background.default, 0.35),
})

// Low-emphasis inline control. Keep the 44px coarse-pointer target floor in
// line with the chips.
const inlineButtonSx = {
  '@media (pointer: coarse)': { minHeight: 44 },
}

const SKELETON_CHIP_WIDTHS: number[][] = [
  [96, 72, 64],
  [120, 64],
  [104, 64],
]

interface MarketAnalyticsFilterBarSkeletonProps {
  // Whether the resolved bar will render its active state (primary-tinted border).
  isActive?: boolean
}

/**
 * Loading placeholder for `MarketAnalyticsFilterBar`, dimensioned to the same
 * card footprint (three rows + footer) as the resolved bar so it doesn't jump
 * when the taxonomy resolves. Also used by the detail-page skeletons so the
 * page-level and component-level placeholders can't drift apart.
 */
export const MarketAnalyticsFilterBarSkeleton: React.FC<MarketAnalyticsFilterBarSkeletonProps> = ({ isActive = false }) => {
  const { t } = useTranslation()

  return (
    <Card elevation={0} sx={filterBarSurfaceSx(isActive)} role="region" aria-label={t('market-analytics:scope_filter_region')} aria-busy="true">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        {SKELETON_CHIP_WIDTHS.map((widths, rowIndex) => (
          <Box key={rowIndex} sx={rowIndex > 0 ? stackedRowSx : rowSx}>
            <Skeleton variant="text" width={FILTER_ROW_LABEL_WIDTH} height={16} sx={{ flexShrink: 0, alignSelf: 'center' }} />
            <Box sx={{ display: 'flex', gap: 1, flex: '1 1 auto' }}>
              {widths.map((width, i) => (
                <Skeleton key={i} variant="rounded" width={width} height={FILTER_CHIP_HEIGHT} sx={{ borderRadius: `${FILTER_CHIP_HEIGHT / 2}px` }} />
              ))}
            </Box>
            <Skeleton variant="text" width={40} height={16} sx={{ flexShrink: 0, alignSelf: 'center' }} />
          </Box>
        ))}
      </CardContent>
      <Box sx={footerSx}>
        <Skeleton variant="text" width={140} sx={{ fontSize: '0.875rem' }} />
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Skeleton variant="circular" width={20} height={20} />
          {/* Matches the small text button's ~30px control height */}
          <Skeleton variant="rounded" width={72} height={30} sx={{ borderRadius: borderRadiusDefault }} />
        </Stack>
      </Box>
    </Card>
  )
}

/**
 * Segment filter for Live Event Analytics: Game Category › Genre › Subgenre,
 * every level multi-select. Each row shows only what is selected there (plus
 * ghost chips for scope inherited from the row above) and a "+ Add" picker, so
 * the card stays the same height however large the taxonomy is.
 *
 * Which games the selection resolves to is decided elsewhere
 * (`resolveEffectiveSubgenreIds`): a game matches the deepest level that has a
 * selection within its own branch, so Category {Casual, Casino} + Genre {Arcade}
 * + Subgenre {Board Games} means Board Games games plus every Casino game. The
 * ghost chips spell that rule out per row.
 */
export const MarketAnalyticsFilterBar: React.FC<MarketAnalyticsFilterBarProps> = ({
  selectedCategoryIds,
  selectedGenreIds,
  selectedSubgenreIds,
  onSetCategories,
  onSetGenres,
  onSetSubgenres,
  onClearCategories,
  onClearGenres,
  onClearSubgenres,
  onClearTaxonomy,
  taxonomyCounts,
  cohortGamesCount,
  isCohortLoading,
}) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const { data: taxonomy, isLoading, isError, refetch } = useGetGenreTaxonomyQuery({ userLanguage })

  const categoryById = useMemo(() => new Map<string, ConventionalCategory>((taxonomy ?? []).map((c) => [c.id, c])), [taxonomy])
  const genreById = useMemo(() => {
    const map = new Map<string, ConventionalGenre>()
    for (const category of taxonomy ?? []) for (const genre of category.genres) map.set(genre.id, genre)
    return map
  }, [taxonomy])
  const subgenreById = useMemo(() => {
    const map = new Map<string, ConventionalSubgenre>()
    for (const category of taxonomy ?? []) for (const genre of category.genres) for (const subgenre of genre.subgenres) map.set(subgenre.id, subgenre)
    return map
  }, [taxonomy])

  // Selected chips per row. Unknown ids (a stale URL param, a taxonomy that
  // dropped a node) are skipped rather than echoed back as raw ids.
  const selectedCategoryOptions = useMemo<SegmentOption[]>(
    () =>
      selectedCategoryIds.flatMap((id) => {
        const category = categoryById.get(id)
        return category ? [{ id, name: category.name, parentId: null, parentName: null }] : []
      }),
    [selectedCategoryIds, categoryById]
  )
  const selectedGenreOptions = useMemo<SegmentOption[]>(
    () =>
      selectedGenreIds.flatMap((id) => {
        const genre = genreById.get(id)
        return genre ? [{ id, name: genre.name, parentId: genre.categoryId, parentName: categoryById.get(genre.categoryId)?.name ?? null }] : []
      }),
    [selectedGenreIds, genreById, categoryById]
  )
  const selectedSubgenreOptions = useMemo<SegmentOption[]>(
    () =>
      selectedSubgenreIds.flatMap((id) => {
        const subgenre = subgenreById.get(id)
        return subgenre ? [{ id, name: subgenre.name, parentId: subgenre.genreId, parentName: genreById.get(subgenre.genreId)?.name ?? null }] : []
      }),
    [selectedSubgenreIds, subgenreById, genreById]
  )

  // What each "+ Add" offers: scoped by the level above, then stripped of nodes
  // with no blueprints so the picker can't lead to an empty page.
  const categoryGroups = useMemo(
    () => pruneAndOrderGroups(categoryOptionGroups(taxonomy ?? []), taxonomyCounts?.categoryCounts, selectedCategoryIds),
    [taxonomy, taxonomyCounts?.categoryCounts, selectedCategoryIds]
  )
  const genreGroups = useMemo(
    () => pruneAndOrderGroups(genreOptionGroups(taxonomy ?? [], selectedCategoryIds), taxonomyCounts?.genreCounts, selectedGenreIds),
    [taxonomy, selectedCategoryIds, taxonomyCounts?.genreCounts, selectedGenreIds]
  )
  const subgenreGroups = useMemo(
    () => pruneAndOrderGroups(subgenreOptionGroups(taxonomy ?? [], selectedCategoryIds, selectedGenreIds), taxonomyCounts?.subgenreCounts, selectedSubgenreIds),
    [taxonomy, selectedCategoryIds, selectedGenreIds, taxonomyCounts?.subgenreCounts, selectedSubgenreIds]
  )

  const inheritedGenreLabels = useMemo(
    () =>
      inheritedGenreScopes(taxonomy ?? [], selectedCategoryIds, selectedGenreIds).map((scope) =>
        t('market-analytics:all_genres_in_category', { category: scope.name })
      ),
    [taxonomy, selectedCategoryIds, selectedGenreIds, t]
  )
  const inheritedSubgenreLabels = useMemo(
    () =>
      inheritedSubgenreScopes(taxonomy ?? [], selectedCategoryIds, selectedGenreIds, selectedSubgenreIds).map((scope) =>
        scope.level === 'genre'
          ? t('market-analytics:all_subgenres_in_genre', { genre: scope.name })
          : t('market-analytics:all_subgenres_in_category', { category: scope.name })
      ),
    [taxonomy, selectedCategoryIds, selectedGenreIds, selectedSubgenreIds, t]
  )

  const isActive = selectedCategoryIds.length + selectedGenreIds.length + selectedSubgenreIds.length > 0

  // Without the taxonomy the rows have nothing to show, so a failed request gets a
  // retry instead of a skeleton that would never resolve.
  if (isError && !taxonomy) {
    return (
      <Card elevation={0} sx={filterBarSurfaceSx(isActive)} role="region" aria-label={t('market-analytics:scope_filter_region')}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" columnGap={2} role="alert">
            <Typography variant="body2" color="text.secondary">
              {t('market-analytics:segment_filter_load_error')}
            </Typography>
            <Button variant="text" size="small" onClick={() => refetch()} sx={{ ...inlineButtonSx, flexShrink: 0 }}>
              {t('market-analytics:retry')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    )
  }

  if (isLoading || !taxonomy) {
    return <MarketAnalyticsFilterBarSkeleton isActive={isActive} />
  }

  const showCohortSkeleton = isCohortLoading ?? true

  const taxonomyHelpContent = (
    <Stack spacing={0.75} sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
        {t('market-analytics:taxonomy_help_title')}
      </Typography>
      <Typography variant="caption">{t('market-analytics:taxonomy_help_narrow')}</Typography>
      <Typography variant="caption">{t('market-analytics:taxonomy_help_all')}</Typography>
    </Stack>
  )

  return (
    <Card elevation={0} sx={filterBarSurfaceSx(isActive)} role="region" aria-label={t('market-analytics:scope_filter_region')}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <SegmentFilterRow
          labelId={CATEGORY_ROW_LABEL_ID}
          label={t('market-analytics:category_filter_label')}
          labelTooltip={t('market-analytics:tooltip_game_category')}
          selectedOptions={selectedCategoryOptions}
          inheritedLabels={[]}
          allLabel={t('market-analytics:all_categories')}
          groups={categoryGroups}
          onApply={onSetCategories}
          onClear={onClearCategories}
          addAriaLabel={t('market-analytics:segment_filter_add_category')}
          clearAriaLabel={t('market-analytics:segment_filter_clear_categories')}
          searchPlaceholder={t('market-analytics:segment_filter_search_categories')}
          containerSx={rowSx}
        />
        <SegmentFilterRow
          labelId={GENRE_ROW_LABEL_ID}
          label={t('market-analytics:genre')}
          labelTooltip={t('market-analytics:tooltip_game_genre')}
          depth={1}
          selectedOptions={selectedGenreOptions}
          inheritedLabels={inheritedGenreLabels}
          allLabel={t('market-analytics:all_genres')}
          groups={genreGroups}
          onApply={onSetGenres}
          onClear={onClearGenres}
          addAriaLabel={t('market-analytics:segment_filter_add_genre')}
          clearAriaLabel={t('market-analytics:segment_filter_clear_genres')}
          searchPlaceholder={t('market-analytics:segment_filter_search_genres')}
          containerSx={stackedRowSx}
        />
        <SegmentFilterRow
          labelId={SUBGENRE_ROW_LABEL_ID}
          label={t('market-analytics:subgenre')}
          labelTooltip={t('market-analytics:tooltip_game_subgenre')}
          depth={2}
          selectedOptions={selectedSubgenreOptions}
          inheritedLabels={inheritedSubgenreLabels}
          allLabel={t('market-analytics:all_subgenres')}
          groups={subgenreGroups}
          onApply={onSetSubgenres}
          onClear={onClearSubgenres}
          addAriaLabel={t('market-analytics:segment_filter_add_subgenre')}
          clearAriaLabel={t('market-analytics:segment_filter_clear_subgenres')}
          searchPlaceholder={t('market-analytics:segment_filter_search_subgenres')}
          containerSx={stackedRowSx}
        />
      </CardContent>

      <Box sx={footerSx}>
        <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
          {typeof cohortGamesCount === 'number' ? (
            // The cohort shrinks as the window grows, so the number moves when the user
            // touches the window chip and not the taxonomy. The tooltip is the only place
            // that explains it.
            <GRTooltip content={t('market-analytics:cohort_filter_games_compared_tooltip')} styles={{ cursor: 'help' }}>
              <Box component="strong" sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                {cohortGamesCount}
              </Box>{' '}
              {t('market-analytics:cohort_filter_games_compared', { count: cohortGamesCount })}
            </GRTooltip>
          ) : showCohortSkeleton ? (
            <Skeleton variant="text" width={140} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />
          ) : (
            <GRTooltip content={t('market-analytics:cohort_filter_games_compared_tooltip')} styles={{ cursor: 'help' }}>
              <Box component="strong" sx={{ color: 'text.primary' }}>
                —
              </Box>{' '}
              {t('market-analytics:cohort_filter_games_compared')}
            </GRTooltip>
          )}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <GRTooltip content={taxonomyHelpContent} styles={{ display: 'inline-flex' }}>
            <IconButton
              size="small"
              aria-label={t('market-analytics:taxonomy_help_aria')}
              sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
          </GRTooltip>
          <Button variant="text" size="small" onClick={onClearTaxonomy} disabled={!isActive} sx={inlineButtonSx}>
            {t('market-analytics:taxonomy_clear_all')}
          </Button>
        </Stack>
      </Box>
    </Card>
  )
}
